import { BaseQueryFn, createApi } from "@reduxjs/toolkit/query/react";
import axios, { AxiosError, AxiosRequestConfig } from "axios";
import { jwtDecode } from "jwt-decode";

import { pseudoLogout } from "../sspSlice";

export interface AxiosBaseQueryArgs extends AxiosRequestConfig {
  blob?: boolean;
  apiRequirements?: string[];
}

export interface ErrorResponse {
  status: number;
  data?: {
    // Todo: TS we have to use any here, because the structure of the error object differs from endpoint to endpoint
    detail?: any; // eslint-disable-line @typescript-eslint/no-explicit-any
    response?: number;
  };
}

const axiosBaseQuery = async (args: AxiosBaseQueryArgs) => {
  const {
    url,
    method = "get",
    data = {},
    params = {},
    headers = {},
    blob = false,
  } = args;

  try {
    return await axios({
      data,
      headers: {
        "X-DKMSIO-Client": process.env.GATSBY_IO_CLIENT_ID,
        ...headers,
      },
      method,
      params,
      url,
      withCredentials: true,
      ...(blob ? { responseType: "blob" } : {}),
    });
  } catch (axiosError) {
    return {
      error: (axiosError as AxiosError).response,
    };
  }
};

const getSession = async (force = false) => {
  const sessionTtl = sessionStorage.getItem("ioSession") || 0;

  if (sessionTtl === 0 || parseInt(sessionTtl, 10) < Date.now() || force) {
    const result = await axiosBaseQuery({
      method: "post",
      url: `${process.env.GATSBY_IO_HOST}/session`,
    });

    const timeToAdd = result && "status" in result && result.status === 200
      ? 28800000
      : 10000;

    sessionStorage.setItem("ioSession", (Date.now() + timeToAdd).toString());
  }
};

const getCsrfToken = async (force = false) => {
  const oldCsrfToken = sessionStorage.getItem("ioCsrfToken");

  if (!force && oldCsrfToken !== null) {
    const oldCsrfTokenPayload = jwtDecode<{ exp: number }>(oldCsrfToken);

    if (oldCsrfTokenPayload.exp > (Math.floor(Date.now() / 1000) + 100)) {
      return oldCsrfToken;
    }
  }

  const csrfTokenResult = await axiosBaseQuery({
    method: "post",
    url: `${process.env.GATSBY_IO_HOST}/token`,
  });

  if (csrfTokenResult && "data" in csrfTokenResult && csrfTokenResult.data?.access_token) {
    sessionStorage.setItem("ioCsrfToken", csrfTokenResult.data.access_token);

    return csrfTokenResult.data.access_token;
  }

  return null;
};

const ioBaseQuery: BaseQueryFn<
  AxiosBaseQueryArgs,
  unknown,
  ErrorResponse,
  {
    forceRenewSession?: boolean;
    forceRenewToken?: boolean;
  }
> = async (
  args,
  api,
  extraOptions = { forceRenewSession: false, forceRenewToken: false },
) => {
  const { forceRenewSession, forceRenewToken } = extraOptions;

  if (args.apiRequirements?.includes("session")) {
    await getSession(forceRenewSession);
  }

  const csrfToken = args.apiRequirements?.includes("csrfToken")
    ? await getCsrfToken(forceRenewToken)
    : null;

  let result = await axiosBaseQuery({
    ...args,
    ...(csrfToken !== null && { headers: { "X-CSRF-Token": csrfToken } }),
  });

  if (result && "error" in result && result.error) {
    switch (result.error.status) {
      case 401:
        api.dispatch(pseudoLogout(true));
        break;

      case 403:
        // only retry once
        if (!forceRenewToken && args.apiRequirements?.includes("csrfToken")) {
          result = await ioBaseQuery(
            args,
            api,
            { ...extraOptions, forceRenewToken: true },
          );
        }

        if (!forceRenewSession && args.apiRequirements?.includes("session")) {
          result = await ioBaseQuery(
            args,
            api,
            { ...extraOptions, forceRenewSession: true },
          );
        }
        break;

      default:
    }
  }

  return result;
};

export default createApi({
  baseQuery: ioBaseQuery,
  endpoints: () => ({}),
  keepUnusedDataFor: 300,
  reducerPath: "io",
  tagTypes: ["SSP"],
});
